// web/src/utils/labels/flow-ivr-satisfaction.ts —— 流程追踪 / 转接 / IVR / 路由 / 满意度 / 停机 / 演练
// 拆自 web/src/utils/labels.ts 的同名分区（内容原样搬运）；由 web/src/utils/labels.ts 以 export * 聚合，导出面对调用方不变。
// 注：原有的知识库场景/应答模式/内容格式/使用效能四组标签已随问答知识库整体下线删除。

import { maskPhone } from '../format';
import type { PillColor } from '../../components/PillTag';

import { FLOW_TRAIL_EVENT_LABELS, FLOW_TRAIL_NODE_LABELS, SCENE_LABEL } from './assist-ai-intent';
import { statusLabel } from './common';

/**
 * 流程运行轨迹事件（biz_flow 运行 trace 的 event）→ [文案, PillColor]。
 * 与 FLOW_TRAIL_EVENT_LABELS（会话流程轨迹 trail，字段 event 取值族不同）区分：两者数据源不同，
 * 不可合并；本表覆盖 node_exit/slot_set/retry/timeout/interrupt/regain/resume/cleanup/arbitration 等运行期事件。
 */
export const FLOW_TRACE_EVENT_LABELS: Record<string, [string, PillColor]> = {
  node_enter: ['进入节点', 'arcoblue'],
  node_exit: ['离开节点', 'arcoblue'],
  slot_set: ['槽位落值', 'cyan'],
  retry: ['重试', 'gold'],
  timeout: ['超时', 'orange'],
  interrupt: ['打岔', 'orange'],
  regain: ['回归', 'green'],
  resume: ['恢复', 'green'],
  transfer: ['转人工', 'red'],
  completed: ['流程完成', 'green'],
  cleanup: ['回收', 'gray'],
  arbitration: ['意图仲裁', 'cyan'],
};

export function flowTraceEventMeta(v?: string): [string, PillColor] {
  return FLOW_TRACE_EVENT_LABELS[v || ''] || [v || '-', 'gray'];
}

/** 业务流程状态（biz_flow.status）→ [文案, PillColor] */
export const FLOW_STATUS_LABELS: Record<string, [string, PillColor]> = {
  draft: ['草稿', 'gray'],
  published: ['已发布', 'green'],
  archived: ['已归档', 'orange'],
};

export function flowStatusMeta(v?: string): [string, PillColor] {
  return FLOW_STATUS_LABELS[v || ''] || [v || '-', 'gray'];
}

/**
 * 流程节点显示名出口（流程设计器节点、坐席流程面板、流程轨迹共用）。
 * 值表为 FLOW_TRAIL_NODE_LABELS：坐席流程面板原「入口/出口/按键菜单」等变体按本表口径收口；
 * 节点自定义中文名（biz_flow_node.name）优先级由调用方判断，本函数只做英文 key 兜底。
 */
export function flowNodeLabel(v?: string): string {
  if (!v) return '-';
  return FLOW_TRAIL_NODE_LABELS[v] || v;
}

/* ---------- 转接 / IVR / 演练 ---------- */

/**
 * 转接面板坐席/话机状态 → [文案, PillColor]。
 * 与 statusLabel 的坐席状态口径不同：转接面板关心「能否转接」，振铃/通话中一律显示「示忙」，
 * 避免坐席误判可转接；坐席状态墙、大屏、坐席管理等展示点仍用 statusLabel。
 */
export const TRANSFER_AGENT_STATE_LABELS: Record<string, [string, PillColor]> = {
  idle: ['示闲', 'green'],
  break: ['小休', 'gold'],
  offline: ['下线', 'gray'],
  ringing: ['示忙', 'arcoblue'],
  talking: ['示忙', 'red'],
  busy: ['示忙', 'red'],
  after_work: ['话后处理', 'gold'],
  acw: ['话后处理', 'gold'],
  after_call_work: ['话后处理', 'gold'],
};

export function transferAgentStateMeta(v?: string): [string, PillColor] {
  return TRANSFER_AGENT_STATE_LABELS[v || ''] || [v || '-', 'gray'];
}

/** IVR 按键目的地类型（ivr_menu_entry.dest_type）→ 中文（图标与分组仍由页面持有） */
export const IVR_DEST_TYPE_LABELS: Record<string, string> = {
  bot: 'AI 机器人',
  queue: '队列',
  extension: '分机',
  menu: '子菜单',
  back: '返回上一级',
  repeat: '重播提示语',
  hangup: '挂断',
};

export function ivrDestTypeLabel(v?: string): string {
  if (!v) return '-';
  return IVR_DEST_TYPE_LABELS[v] || v;
}

/**
 * 呼叫路由/时段的目的地类型（route_rule.dest_type）→ 中文。
 * 与上面 IVR 按键目的地是**两个枚举**（这里是"这通电话去哪"，router 的取值集合不含 back/repeat），
 * 故单列一张表；两者 `menu` 刻意用词不同（路由侧指向一个菜单整页，IVR 侧指下钻一层子菜单）。
 */
export const ROUTE_DEST_TYPE_LABELS: Record<string, string> = {
  bot: 'AI 机器人',
  queue: '队列',
  extension: '分机',
  menu: 'IVR 菜单',
  hangup: '挂断',
};

export function routeDestTypeLabel(v?: string): string {
  if (!v) return '-';
  return ROUTE_DEST_TYPE_LABELS[v] || v;
}

/** 满意度场景（call/chat）→ 中文：与 AI 能力场景（labels.ts 的 SCENE_LABEL）是两个域，勿混用 */
export const SATISFACTION_SCENE_LABELS: Record<string, string> = {
  call: '电话',
  chat: '在线',
};

export function satisfactionSceneLabel(scene?: string): string {
  if (!scene) return '-';
  return SATISFACTION_SCENE_LABELS[scene] || scene;
}

/** 停机状态（query_suspend 辅助卡状态码）→ [文案, PillColor]（处理指引文案仍由页面持有） */
export const SUSPEND_STATE_LABELS: Record<string, [string, PillColor]> = {
  arrears: ['欠费停机', 'orange'],
  auth_failed: ['二次认证未通过', 'orange'],
  fraud: ['涉诈关停', 'red'],
  self: ['主动停机', 'gray'],
  loss: ['挂失停机', 'gray'],
  none: ['状态正常', 'green'],
};

export function suspendStateMeta(v?: string): [string, PillColor] {
  return SUSPEND_STATE_LABELS[v || ''] || [v || '-', 'gray'];
}

/** 演练对话角色（coach 消息 role）→ 中文 */
export const COACH_ROLE_LABELS: Record<string, string> = {
  customer: 'AI 客户',
  agent: '我 · 坐席',
};

export function coachRoleLabel(v?: string): string {
  if (!v) return '-';
  return COACH_ROLE_LABELS[v] || v;
}

/* ============================================================
 * 统一出口（各域共用）
 *
 * 页面不得再写 `v ? '是' : '否'`、`v ? '启用' : '停用'` 之类的三元自拼，也不得再起
 * 页级色表；下列出口与别名覆盖各域侦察提出的枚举，命名以此处实际导出为准。
 * ============================================================ */

/** 布尔值 → 「是/否」：true/'true'/'1'/'yes'/'on' → 是；false/'false'/'0'/'no'/'off' → 否；空值 '-'，其他原样 */
export function boolLabel(v?: boolean | string | number | null): string {
  if (v === undefined || v === null || v === '') return '-';
  if (typeof v === 'boolean') return v ? '是' : '否';
  const s = String(v).trim();
  if (!s) return '-';
  const k = s.toLowerCase();
  if (k === 'true' || k === '1' || k === 'yes' || k === 'on') return '是';
  if (k === 'false' || k === '0' || k === 'no' || k === 'off') return '否';
  return s;
}

/**
 * 开关值 → 「启用/停用」：true/'true'/'1'/'enabled'/'active'/'on' → 启用；
 * false/'false'/'0'/'disabled'/'inactive'/'off' → 停用；空值 '-'，其他原样。
 */
export function enableLabel(v?: boolean | string | number | null): string {
  if (v === undefined || v === null || v === '') return '-';
  if (typeof v === 'boolean') return v ? '启用' : '停用';
  const s = String(v).trim();
  if (!s) return '-';
  const k = s.toLowerCase();
  if (k === 'true' || k === '1' || k === 'enabled' || k === 'active' || k === 'on') return '启用';
  if (k === 'false' || k === '0' || k === 'disabled' || k === 'inactive' || k === 'off') return '停用';
  return s;
}

